import type { QuoteLine } from '../api/types'
import { numberValue } from './numbers'

/**
 * A quote line as the equipment list shows it: parents, their selected configuration options
 * (rendered as included sub-rows) and accessories, in display order.
 */
export type EquipmentLine = QuoteLine & {
  configuration_group?: string
  parent_description?: string
  parent_equipment_details?: string[]
}

type ConfigurationItem = Omit<Partial<QuoteLine>, 'product_id'> & {
  group: string
  product_id: number | null
  equipment_description?: string | null
}

/**
 * The chosen option of each configured group, as display rows. Quote lines store each group's
 * option id and keep display facts only for options that are catalog products, so a choice with
 * no product (a voltage on the same model) shows its recorded label.
 */
export function chosenConfigurationItems(line: QuoteLine): ConfigurationItem[] {
  const items = (line.configuration_items ?? []) as ConfigurationItem[]
  return Object.entries(line.configuration ?? {}).map(([group, choice]) => {
    const candidates = items.filter((item) => item.group === group)
    const item =
      candidates.find((candidate) => String(candidate.product_id) === String(choice)) ??
      (candidates.length === 1 ? candidates[0] : undefined)
    return (
      item ?? {
        group,
        product_id: null,
        product_uuid: null,
        description: line.configuration_display?.[group] ?? String(choice),
        model: null,
        manufacturer: null,
        manufacturer_uuid: null,
        primary_asset: null,
        equipment_details: [],
      }
    )
  })
}

/** Flatten lines into display order: each parent, its chosen configuration, then its accessories. */
export function equipmentRows(lines: QuoteLine[]): EquipmentLine[] {
  const rows: EquipmentLine[] = []
  const ids = new Set(lines.map((line) => line.id))
  const children = new Map<string, QuoteLine[]>()
  for (const line of lines) {
    if (!line.parent_line_id) continue
    const siblings = children.get(line.parent_line_id) ?? []
    siblings.push(line)
    children.set(line.parent_line_id, siblings)
  }
  const append = (line: EquipmentLine) => {
    rows.push(line)
    for (const item of chosenConfigurationItems(line)) {
      rows.push({
        ...line,
        ...item,
        primary_asset: item.primary_asset ?? null,
        equipment_description: item.equipment_description || item.description || null,
        equipment_details: item.equipment_details || [],
        id: `configuration:${line.id}:${item.group}`,
        parent_line_id: line.id,
        configuration_group: item.group,
        configuration: {},
        configuration_items: [],
        configuration_display: {},
        quantity: line.quantity,
        parent_description: line.equipment_description || line.description,
        parent_equipment_details: line.equipment_details || [],
      })
    }
    children.get(line.id)?.forEach((child) =>
      append({
        ...child,
        parent_description: line.equipment_description || line.description,
        parent_equipment_details: line.equipment_details || [],
      }),
    )
  }
  lines.filter((line) => !line.parent_line_id || !ids.has(line.parent_line_id)).forEach(append)
  return rows
}

/** Outline numbers in display order: parents 1, 2, 3; their sub-lines 1.1, 1.2. */
export function quoteLineNumbers(lines: Pick<QuoteLine, 'id' | 'parent_line_id'>[]) {
  const numbers = new Map<string, string>()
  const childCounts = new Map<string, number>()
  let rootCount = 0
  for (const line of lines) {
    const parentNumber = line.parent_line_id ? numbers.get(line.parent_line_id) : undefined
    if (!parentNumber) {
      rootCount += 1
      numbers.set(line.id, String(rootCount))
      continue
    }
    const childCount = (childCounts.get(line.parent_line_id!) || 0) + 1
    childCounts.set(line.parent_line_id!, childCount)
    numbers.set(line.id, `${parentNumber}.${childCount}`)
  }
  return numbers
}

/** One line's unit sell, unit Net, Profit, Margin %, markup % and sell total. */
export function quoteLinePricing(line: QuoteLine) {
  const sell = numberValue(line.sell_amount ?? line.list_amount)
  const net = numberValue(line.derived_net_amount ?? line.source_net_amount ?? line.list_amount)
  return {
    sell,
    net,
    profit: numberValue(line.totals.profit),
    margin: numberValue(line.totals.margin_percent),
    markup: net > 0 ? ((sell - net) / net) * 100 : null,
    sellTotal: numberValue(line.totals.sell),
  }
}

/** Manufacturer discount chain in trade notation: [50, 10, 5] → "50/10/5". */
export function formatDiscountChain(chain: Array<number | string> | null | undefined) {
  if (!chain?.length) return null
  return chain
    .map((component) => Number(component).toLocaleString('en-US', { maximumFractionDigits: 4 }))
    .join('/')
}

/** The line's description with its manufacturer and model removed, since both show beside it. */
export function quoteLineDetail(line: QuoteLine) {
  let detail = line.equipment_description || line.description
  for (const identity of [line.manufacturer, line.model]) {
    if (!identity) continue
    const escaped = identity.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    detail = detail.replace(new RegExp(`(^|\\s)${escaped}(?=$|[\\s,;:])`, 'gi'), '$1')
  }
  detail = detail.replace(/\s{2,}/g, ' ').replace(/^[\s,;:|-]+|[\s,;:|-]+$/g, '')
  return detail || line.equipment_details?.slice(0, 2).join('; ') || line.taxonomy_label || ''
}
